export type SynchronizedCurrentImage = {
    version: number;
    imageKey: string;
    imageUrl: string;
    width: number;
    height: number;
    imageName: string;
    rating: number;
};

export type SynchronizedCurrentImageSnapshot = {
    version: number;
    image: SynchronizedCurrentImage | null;
    isLocked: boolean;
    lockedImageKey: string | null;
};

export type GuestViewerOptions = {
    snapshotUrl?: string;
    eventsUrl?: string;
    viewportReportUrl?: string;
    host?: HTMLElement;
    onConnectionChanged?: (isConnected: boolean) => void;
};

export type GuestViewerController = {
    resetViewport(): void;
    dispose(): void;
};

type Point = { x: number; y: number };
type ViewportState = { zoomRatio: number; focusX: number; focusY: number };

const maximumScaleRatio = 8;
const viewportReportThrottleMs = 90;
const doubleTapMaximumIntervalMs = 500;
const tapMovementMaximumDistancePixels = 14;
const doubleTapMaximumDistancePixels = 20;
const viewportMarginPixels = 25;

export function mountGuestViewer(options: GuestViewerOptions = {}): GuestViewerController {
    const snapshotUrl = options.snapshotUrl ?? '/api/guest/current-image';
    const eventsUrl = options.eventsUrl ?? '/api/guest/current-image/events';
    const viewportReportUrl = options.viewportReportUrl ?? '/api/guest/viewport';
    const host = options.host ?? document.body;
    const abortController = new AbortController();

    const surface = document.createElement('section');
    surface.className = 'guest-viewer';
    surface.setAttribute('aria-label', 'Synchronized image viewer');

    const imageElement = document.createElement('img');
    imageElement.className = 'guest-image';
    imageElement.alt = '';
    imageElement.draggable = false;
    imageElement.hidden = true;

    const ratingSurface = document.createElement('div');
    ratingSurface.className = 'guest-image-rating';
    ratingSurface.setAttribute('aria-live', 'polite');
    ratingSurface.hidden = true;

    const statusSurface = document.createElement('div');
    statusSurface.className = 'guest-status';
    statusSurface.setAttribute('role', 'status');
    statusSurface.setAttribute('aria-live', 'polite');
    statusSurface.textContent = 'Waiting for the Admin to select an image...';

    const disconnectedIndicator = document.createElement('div');
    disconnectedIndicator.className = 'guest-disconnected-indicator';
    disconnectedIndicator.setAttribute('role', 'img');
    disconnectedIndicator.setAttribute('aria-label', 'Disconnected from server');
    disconnectedIndicator.title = 'Disconnected from server';
    disconnectedIndicator.textContent = '⚠';
    disconnectedIndicator.hidden = true;

    surface.append(imageElement, ratingSurface, statusSurface, disconnectedIndicator);
    host.append(surface);

    let eventSource: EventSource | null = null;
    let disposed = false;
    let latestVersion = -1;
    let imageLoadToken = 0;
    let naturalWidth = 0;
    let naturalHeight = 0;
    let scale = 1;
    let offsetX = 0;
    let offsetY = 0;
    let viewportState: ViewportState = { zoomRatio: 1, focusX: 0.5, focusY: 0.5 };
    let viewportSessionId: string | null = null;
    let presentedImageKey: string | null = null;
    let viewportReportTimeoutId: number | null = null;
    let viewportReportAbortController: AbortController | null = null;
    const pointers = new Map<number, Point>();
    let panPointerId: number | null = null;
    let lastPanPoint: Point | null = null;
    let lastPinchDistance: number | null = null;
    let tapPointerId: number | null = null;
    let tapStartPoint: Point | null = null;
    let lastTapTimestampMs = 0;
    let lastTapPoint: Point | null = null;

    function clamp(value: number, minimum: number, maximum: number): number {
        return Math.min(maximum, Math.max(minimum, value));
    }

    function setConnected(isConnected: boolean): void {
        disconnectedIndicator.hidden = isConnected;
        options.onConnectionChanged?.(isConnected);
    }

    function normalizeViewportSession(payload: unknown): string | null {
        if (payload == null || typeof payload !== 'object') {
            return null;
        }

        const candidate = payload as Record<string, unknown>;
        const sessionId = candidate.sessionId ?? candidate.SessionId;
        return typeof sessionId === 'string' && sessionId.length > 0 ? sessionId : null;
    }

    function getViewportDimensions(): { width: number; height: number } {
        const width = Math.max(1, Math.round(window.visualViewport?.width ?? surface.clientWidth));
        const height = Math.max(1, Math.round(window.visualViewport?.height ?? surface.clientHeight));
        return { width, height };
    }

    async function sendViewportReportAsync(): Promise<void> {
        if (viewportSessionId == null || presentedImageKey == null || presentedImageKey.length === 0 || imageElement.hidden) {
            return;
        }

        viewportReportAbortController?.abort();
        viewportReportAbortController = new AbortController();
        const { width, height } = getViewportDimensions();
        const payload = {
            sessionId: viewportSessionId,
            imageKey: presentedImageKey,
            viewportWidth: width,
            viewportHeight: height,
            zoomRatio: viewportState.zoomRatio,
            focusX: viewportState.focusX,
            focusY: viewportState.focusY,
            reportedAtUtc: new Date().toISOString()
        };

        try {
            await fetch(viewportReportUrl, {
                method: 'POST',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload),
                signal: viewportReportAbortController.signal,
                keepalive: true,
                cache: 'no-store'
            });
        } catch (error) {
            if ((error as { name?: string }).name !== 'AbortError') {
                console.warn('Failed to send guest viewport report.', error);
            }
        }
    }

    function queueViewportReport(): void {
        if (viewportReportTimeoutId != null) {
            window.clearTimeout(viewportReportTimeoutId);
        }

        viewportReportTimeoutId = window.setTimeout(() => {
            viewportReportTimeoutId = null;
            void sendViewportReportAsync();
        }, viewportReportThrottleMs);
    }

    function getFitScale(): number {
        const margin = viewportMarginPixels * 2;
        const availableWidth = Math.max(1, surface.clientWidth - margin);
        const availableHeight = Math.max(1, surface.clientHeight - margin);
        return Math.min(
            availableWidth / Math.max(1, naturalWidth),
            availableHeight / Math.max(1, naturalHeight));
    }

    function clampAndRender(): void {
        const scaledWidth = naturalWidth * scale;
        const scaledHeight = naturalHeight * scale;
        offsetX = scaledWidth <= surface.clientWidth - viewportMarginPixels * 2
            ? (surface.clientWidth - scaledWidth) * 0.5
            : clamp(offsetX, surface.clientWidth - viewportMarginPixels - scaledWidth, viewportMarginPixels);
        offsetY = scaledHeight <= surface.clientHeight - viewportMarginPixels * 2
            ? (surface.clientHeight - scaledHeight) * 0.5
            : clamp(offsetY, surface.clientHeight - viewportMarginPixels - scaledHeight, viewportMarginPixels);

        imageElement.style.width = `${naturalWidth}px`;
        imageElement.style.height = `${naturalHeight}px`;
        imageElement.style.transform = `translate(${offsetX}px, ${offsetY}px) scale(${scale})`;
    }

    function captureViewportState(): void {
        if (naturalWidth <= 0 || naturalHeight <= 0 || scale <= 0) {
            return;
        }

        const fitScale = getFitScale();
        const centerX = surface.clientWidth * 0.5;
        const centerY = surface.clientHeight * 0.5;
        viewportState = {
            zoomRatio: Math.max(1, scale / fitScale),
            focusX: clamp((centerX - offsetX) / scale / naturalWidth, 0, 1),
            focusY: clamp((centerY - offsetY) / scale / naturalHeight, 0, 1)
        };
        queueViewportReport();
    }

    function applyTransform(): void {
        if (naturalWidth <= 0 || naturalHeight <= 0) {
            return;
        }

        const fitScale = getFitScale();
        scale = clamp(fitScale * viewportState.zoomRatio, fitScale, fitScale * maximumScaleRatio);
        offsetX = surface.clientWidth * 0.5 - viewportState.focusX * naturalWidth * scale;
        offsetY = surface.clientHeight * 0.5 - viewportState.focusY * naturalHeight * scale;
        clampAndRender();
    }

    function resetViewport(): void {
        viewportState = { zoomRatio: 1, focusX: 0.5, focusY: 0.5 };
        applyTransform();
    }

    function zoomByFactor(factor: number, anchor: Point): void {
        if (naturalWidth <= 0 || naturalHeight <= 0 || !Number.isFinite(factor) || factor <= 0) {
            return;
        }

        const fitScale = getFitScale();
        const nextScale = clamp(scale * factor, fitScale, fitScale * maximumScaleRatio);
        const imageX = (anchor.x - offsetX) / scale;
        const imageY = (anchor.y - offsetY) / scale;
        scale = nextScale;
        offsetX = anchor.x - imageX * nextScale;
        offsetY = anchor.y - imageY * nextScale;
        clampAndRender();
        captureViewportState();
    }

    function getPinchDistance(): number | null {
        const active = [...pointers.values()];
        return active.length < 2 ? null : Math.hypot(active[1].x - active[0].x, active[1].y - active[0].y);
    }

    function getPinchCenter(): Point | null {
        const active = [...pointers.values()];
        return active.length < 2 ? null : {
            x: (active[0].x + active[1].x) * 0.5,
            y: (active[0].y + active[1].y) * 0.5
        };
    }

    function onPointerDown(event: PointerEvent): void {
        if (imageElement.hidden) {
            return;
        }

        event.preventDefault();
        const point = { x: event.clientX, y: event.clientY };
        pointers.set(event.pointerId, point);
        surface.setPointerCapture(event.pointerId);
        if (pointers.size === 1) {
            panPointerId = event.pointerId;
            lastPanPoint = point;
            lastPinchDistance = null;
            tapPointerId = event.pointerType === 'touch' ? event.pointerId : null;
            tapStartPoint = event.pointerType === 'touch' ? point : null;
        } else {
            panPointerId = null;
            lastPanPoint = null;
            lastPinchDistance = getPinchDistance();
            tapPointerId = null;
            tapStartPoint = null;
        }
    }

    function onPointerMove(event: PointerEvent): void {
        if (!pointers.has(event.pointerId)) {
            return;
        }

        event.preventDefault();
        const point = { x: event.clientX, y: event.clientY };
        pointers.set(event.pointerId, point);
        if (tapPointerId === event.pointerId
            && tapStartPoint != null
            && Math.hypot(point.x - tapStartPoint.x, point.y - tapStartPoint.y) > tapMovementMaximumDistancePixels) {
            tapPointerId = null;
            tapStartPoint = null;
        }

        if (pointers.size >= 2) {
            const distance = getPinchDistance();
            const center = getPinchCenter();
            if (distance != null && lastPinchDistance != null && lastPinchDistance > 0 && center != null) {
                zoomByFactor(distance / lastPinchDistance, center);
            }
            lastPinchDistance = distance;
            return;
        }

        if (panPointerId === event.pointerId && lastPanPoint != null) {
            offsetX += point.x - lastPanPoint.x;
            offsetY += point.y - lastPanPoint.y;
            lastPanPoint = point;
            clampAndRender();
            captureViewportState();
        }
    }

    function stopPointerInteraction(event: PointerEvent): void {
        if (!pointers.has(event.pointerId)) {
            return;
        }

        const point = { x: event.clientX, y: event.clientY };
        if (tapPointerId === event.pointerId && tapStartPoint != null) {
            const timestampMs = performance.now();
            const isDoubleTap = lastTapPoint != null
                && timestampMs - lastTapTimestampMs <= doubleTapMaximumIntervalMs
                && Math.hypot(point.x - lastTapPoint.x, point.y - lastTapPoint.y) <= doubleTapMaximumDistancePixels;
            if (isDoubleTap) {
                const fitScale = getFitScale();
                if (scale > fitScale * 1.05) {
                    resetViewport();
                } else {
                    zoomByFactor(2, point);
                }
                lastTapTimestampMs = 0;
                lastTapPoint = null;
            } else {
                lastTapTimestampMs = timestampMs;
                lastTapPoint = point;
            }
        }

        pointers.delete(event.pointerId);
        tapPointerId = null;
        tapStartPoint = null;
        if (pointers.size === 1) {
            const remaining = [...pointers.entries()][0];
            panPointerId = remaining[0];
            lastPanPoint = remaining[1];
        } else {
            panPointerId = null;
            lastPanPoint = null;
        }
        lastPinchDistance = getPinchDistance();
    }

    function onWheel(event: WheelEvent): void {
        if (imageElement.hidden) {
            return;
        }

        event.preventDefault();
        zoomByFactor(Math.exp(-event.deltaY * 0.0015), { x: event.clientX, y: event.clientY });
    }

    function onDoubleClick(event: MouseEvent): void {
        if (imageElement.hidden) {
            return;
        }

        const fitScale = getFitScale();
        if (scale > fitScale * 1.05) {
            resetViewport();
        } else {
            zoomByFactor(2, { x: event.clientX, y: event.clientY });
        }
    }

    function normalizeNonNegativeInteger(value: unknown): number | null {
        return typeof value === 'number' && Number.isSafeInteger(value) && value >= 0 ? value : null;
    }

    function normalizeSnapshot(payload: unknown): SynchronizedCurrentImageSnapshot | null {
        if (payload == null || typeof payload !== 'object') {
            return null;
        }

        const rawSnapshot = payload as Record<string, unknown>;
        const version = normalizeNonNegativeInteger(rawSnapshot.version ?? rawSnapshot.Version);
        const rawImage = rawSnapshot.image ?? rawSnapshot.Image;
        const isLockedValue = rawSnapshot.isLocked ?? rawSnapshot.IsLocked;
        const lockedImageKeyValue = rawSnapshot.lockedImageKey ?? rawSnapshot.LockedImageKey;
        if (version == null) {
            return null;
        }
        if (isLockedValue != null && typeof isLockedValue !== 'boolean') {
            return null;
        }
        if (lockedImageKeyValue != null && typeof lockedImageKeyValue !== 'string') {
            return null;
        }

        const isLocked = isLockedValue === true;
        const lockedImageKey = typeof lockedImageKeyValue === 'string' && lockedImageKeyValue.length > 0
            ? lockedImageKeyValue
            : null;

        if (rawImage == null) {
            return { version, image: null, isLocked, lockedImageKey };
        }
        if (typeof rawImage !== 'object') {
            return null;
        }

        const image = rawImage as Record<string, unknown>;
        const imageVersion = normalizeNonNegativeInteger(image.version ?? image.Version);
        const imageKey = image.imageKey ?? image.ImageKey;
        const imageUrl = image.imageUrl ?? image.ImageUrl;
        const width = normalizeNonNegativeInteger(image.width ?? image.Width);
        const height = normalizeNonNegativeInteger(image.height ?? image.Height);
        const imageName = image.imageName ?? image.ImageName;
        const rating = normalizeNonNegativeInteger(image.rating ?? image.Rating);
        if (imageVersion == null || typeof imageKey !== 'string' || typeof imageUrl !== 'string'
            || width == null || width <= 0 || height == null || height <= 0
            || typeof imageName !== 'string' || rating == null || rating > 5) {
            return null;
        }

        return {
            version,
            image: { version: imageVersion, imageKey, imageUrl, width, height, imageName, rating },
            isLocked,
            lockedImageKey
        };
    }

    function applySnapshot(snapshot: SynchronizedCurrentImageSnapshot): void {
        if (snapshot.version === latestVersion) {
            return;
        }
        latestVersion = snapshot.version;

        if (snapshot.image == null) {
            if (imageElement.hidden) {
                statusSurface.textContent = 'Waiting for the Admin to select an image...';
                statusSurface.hidden = false;
            }
            return;
        }

        const image = snapshot.image;
        const loadToken = ++imageLoadToken;
        const preload = new Image();
        preload.decoding = 'async';
        preload.onload = () => {
            if (disposed || loadToken !== imageLoadToken) {
                return;
            }

            naturalWidth = preload.naturalWidth || image.width;
            naturalHeight = preload.naturalHeight || image.height;
            presentedImageKey = image.imageKey;
            imageElement.src = image.imageUrl;
            imageElement.alt = image.imageName;
            imageElement.hidden = false;
            ratingSurface.textContent = image.rating > 0 ? '★'.repeat(image.rating) : '';
            ratingSurface.hidden = image.rating === 0;
            statusSurface.hidden = true;
            applyTransform();
            queueViewportReport();
        };
        preload.onerror = () => {
            if (loadToken === imageLoadToken) {
                statusSurface.textContent = 'The synchronized image could not be loaded. Waiting for the next image...';
                statusSurface.hidden = false;
            }
        };
        preload.src = image.imageUrl;
    }

    async function fetchInitialSnapshot(): Promise<void> {
        const response = await fetch(snapshotUrl, {
            headers: { Accept: 'application/json' },
            cache: 'no-store',
            signal: abortController.signal
        });
        if (!response.ok) {
            throw new Error(`Guest current image request failed with status ${response.status}.`);
        }

        const snapshot = normalizeSnapshot(await response.json() as unknown);
        if (snapshot == null) {
            throw new Error('Guest current image response was malformed.');
        }
        applySnapshot(snapshot);
        setConnected(true);
    }

    function connectEvents(): void {
        if (disposed) {
            return;
        }

        eventSource = new EventSource(eventsUrl);
        eventSource.addEventListener('guest-viewport-session', event => {
            try {
                const parsed = JSON.parse((event as MessageEvent<string>).data) as unknown;
                const sessionId = normalizeViewportSession(parsed);
                if (sessionId == null) {
                    return;
                }

                viewportSessionId = sessionId;
                queueViewportReport();
            } catch (error) {
                console.warn('Guest viewport session event was malformed.', error);
            }
        });
        eventSource.addEventListener('current-image', event => {
            try {
                const snapshot = normalizeSnapshot(JSON.parse((event as MessageEvent<string>).data) as unknown);
                if (snapshot != null) {
                    applySnapshot(snapshot);
                }
            } catch (error) {
                console.warn('Guest current image event was malformed.', error);
            }
        });
        eventSource.onopen = () => setConnected(true);
        eventSource.onerror = () => setConnected(false);
    }

    function onResize(): void {
        applyTransform();
        queueViewportReport();
    }

    function onKeyDown(event: KeyboardEvent): void {
        if (event.key === 'Escape') {
            resetViewport();
        }
    }

    surface.addEventListener('pointerdown', onPointerDown);
    surface.addEventListener('pointermove', onPointerMove);
    surface.addEventListener('pointerup', stopPointerInteraction);
    surface.addEventListener('pointercancel', stopPointerInteraction);
    surface.addEventListener('lostpointercapture', stopPointerInteraction);
    surface.addEventListener('wheel', onWheel, { passive: false });
    surface.addEventListener('dblclick', onDoubleClick);
    window.addEventListener('resize', onResize);
    window.visualViewport?.addEventListener('resize', onResize);
    window.addEventListener('keydown', onKeyDown);

    void fetchInitialSnapshot().then(
        () => connectEvents(),
        error => {
            if (!abortController.signal.aborted) {
                setConnected(false);
                console.warn('Failed to load the Guest current image.', error);
            }
            connectEvents();
        });

    return {
        resetViewport,
        dispose(): void {
            if (disposed) {
                return;
            }
            disposed = true;
            abortController.abort();
            eventSource?.close();
            window.removeEventListener('resize', onResize);
            window.visualViewport?.removeEventListener('resize', onResize);
            window.removeEventListener('keydown', onKeyDown);
            surface.remove();
        }
    };
}
